import React, { useState } from "react";
import { X, MapPin, Bookmark, Briefcase, Clock, Zap, CheckCircle2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fmtSalary, timeAgo } from "@/components/JobCard";

export default function JobDrawer({ job, open, onClose, saved, onToggleSave, onApply, applied }) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open || !job) return null;

  const handleApply = async () => {
    setSubmitting(true);
    await onApply(job, note);
    setSubmitting(false);
    setNote("");
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="job-detail-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-lg glass border-l border-teal-500/20 overflow-y-auto animate-in slide-in-from-right duration-300">
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-700 flex items-center justify-center font-display font-bold text-xl text-teal-300">
                {job.company?.[0] || "W"}
              </div>
              <div>
                <h2 className="font-display font-bold text-xl text-slate-100">{job.title}</h2>
                <p className="text-slate-400 flex items-center gap-1.5 text-sm"><Building2 size={14} /> {job.company}</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="drawer-close" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <Stat icon={MapPin} label="Location" value={job.location} />
            <Stat icon={Briefcase} label="Type" value={job.job_type} />
            <Stat icon={Clock} label="Posted" value={timeAgo(job.posted_at)} />
            <Stat icon={Zap} label="Match" value={`${job.match || 90}%`} accent />
          </div>

          <div className="mt-6 flex items-center justify-between rounded-xl bg-slate-950/50 border border-teal-500/15 p-4">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Salary Range</div>
              <div className="text-teal-300 font-mono text-lg font-bold">{fmtSalary(job.salary_min, job.salary_max)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">Work Mode</div>
              <div className="text-blue-300 font-semibold">{job.work_mode}</div>
            </div>
          </div>

          <div className="mt-6">
            <h4 className="font-display font-semibold text-slate-200 mb-2">About the role</h4>
            <p className="text-slate-400 text-sm leading-relaxed">{job.description}</p>
          </div>

          <div className="mt-6">
            <h4 className="font-display font-semibold text-slate-200 mb-2">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {(job.skills || []).map((s) => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-slate-800/80 text-cyan-300 border border-cyan-500/20">{s}</span>
              ))}
            </div>
          </div>

          {!applied && (
            <div className="mt-6">
              <h4 className="font-display font-semibold text-slate-200 mb-2">Cover note (optional)</h4>
              <Textarea
                data-testid="apply-cover-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tell them why you're a great fit..."
                className="bg-slate-950/50 border-slate-700 text-slate-200 min-h-[90px]"
              />
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {applied ? (
              <div className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-teal-500/15 border border-teal-500/30 text-teal-300 py-3 font-semibold" data-testid="applied-badge">
                <CheckCircle2 size={18} /> Applied
              </div>
            ) : (
              <Button data-testid="job-card-apply-button" onClick={handleApply} disabled={submitting}
                className="flex-1 bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 py-6">
                {submitting ? "Submitting..." : "Apply Now"}
              </Button>
            )}
            <Button variant="outline" onClick={() => onToggleSave(job)} data-testid="drawer-bookmark"
              className={`border-slate-700 py-6 ${saved ? "text-teal-400 border-teal-500/40" : "text-slate-300"}`}>
              <Bookmark size={18} fill={saved ? "currentColor" : "none"} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg bg-slate-950/40 border border-slate-800 p-3">
      <div className="text-xs text-slate-500 flex items-center gap-1"><Icon size={12} /> {label}</div>
      <div className={`text-sm font-medium mt-0.5 truncate ${accent ? "text-teal-300" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}
